import React, { useCallback, useMemo, useState } from "react";

import { useSelector } from "react-redux";
import { useDebounce } from "@react-hook/debounce";

import AgGridTable from "components/AgGrid/AgGridContainer";
import TableWrapper from "components/AgGrid/TableWrapper";
import { SlimSecondaryButton } from "components/Button";
import { SecondaryButton } from "components/Form";
import { Column, Row } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { SmallText } from "components/Text";

import { AgGridTables } from "constants/aggrid";
import { SideLoadedBusinessColumnDefs } from "constants/columnDefinitions/sideLoadedBusiness";

import { EMPTY_ARRAY } from "lib";
import {
  getSideLoadedBusinesses,
  pickSideLoadedSearchResults,
  searchSideLoadedBusinessesWithMatches,
  selectSideLoadedBusinessesGridList,
} from "selectors";
import SearchInput from "components/SearchInput";

import styled from "styled-components/macro";

import {
  getSideLoadedBusinessRowId,
  useSideLoadedBusinessGridContext,
} from "./useSideLoadedBusinessGridContext";

interface SideLoadedBusinessTableProps {
  initialSearchTerm?: string;
  onClose: () => void;
  onSelect: (recordId: string) => void;
}

export const SideLoadedBusinessTableWrapper = styled(TableWrapper)(
  ({ minHeight = 400 }) => `
  min-height: ${minHeight}px;
`,
);
const SideLoadedBusinessTable = ({
  initialSearchTerm = "",
  onClose,
  onSelect,
}: SideLoadedBusinessTableProps): React.JSX.Element => {
  // Use our Fuse search results, rather than the embedded AgGrid one in order to give
  // consistent results.
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(
    searchTerm,
    300,
  );
  React.useEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [setSearchTerm, debouncedSearchTerm]);
  React.useEffect(() => {
    setDebouncedSearchTerm(searchTerm);
  }, [setDebouncedSearchTerm, searchTerm]);

  const [showIgnored, setShowIgnored] = useState(false);

  const sideLoadedBusinesses: Record<string, SideLoadedBusiness> = useSelector(
    getSideLoadedBusinesses,
  );

  // Always fetch ignored records too, so toggling doesn't have to re-query -
  // and so ignoredCount below can say how many of *these* results are ignored,
  // rather than how many are ignored across the whole (unsearched) data set.
  const sideLoadStrictResults = useSelector(
    searchSideLoadedBusinessesWithMatches(debouncedSearchTerm, true, true),
  );
  const sideLoadLiberalResults = useSelector(
    searchSideLoadedBusinessesWithMatches(debouncedSearchTerm, false, true),
  );
  const sideLoadResults = pickSideLoadedSearchResults(
    sideLoadStrictResults,
    sideLoadLiberalResults,
    showIgnored,
  );

  const sideLoadIds = useMemo(
    () =>
      debouncedSearchTerm
        ? sideLoadResults
            .filter(result => showIgnored || !result.isIgnored)
            .map(result => result.id)
        : null,
    [sideLoadResults, debouncedSearchTerm, showIgnored],
  );

  const matchedFieldsById = useMemo(
    () =>
      new Map(sideLoadResults.map(result => [result.id, result.matchedFields])),
    [sideLoadResults],
  );

  // Reuse the same validationIssues the Manage grid gets from this selector,
  // rather than recomputing them here.
  const gridList = useSelector(selectSideLoadedBusinessesGridList);
  const validationIssuesById = useMemo(
    () =>
      new Map(
        gridList.map(business => [business.id, business.validationIssues]),
      ),
    [gridList],
  );

  // With a search term active, count ignored records among the matches - the
  // button should promise exactly how many rows toggling it on will add, not
  // the total ignored count across records the search has already excluded.
  const ignoredCount = useMemo(() => {
    if (debouncedSearchTerm) {
      return sideLoadResults.filter(result => result.isIgnored).length;
    }
    return Object.values(sideLoadedBusinesses).filter(
      (slb: SideLoadedBusiness) => slb.isIgnored,
    ).length;
  }, [debouncedSearchTerm, sideLoadResults, sideLoadedBusinesses]);

  // Show all results when there is no search term.
  const rowData = useMemo(() => {
    if (!sideLoadIds) {
      return Object.values(sideLoadedBusinesses)
        .filter((slb: SideLoadedBusiness) => showIgnored || !slb.isIgnored)
        .map(business => ({
          ...business,
          matchedFields: EMPTY_ARRAY,
          validationIssues:
            validationIssuesById.get(business.id) ?? EMPTY_ARRAY,
        }));
    }
    // keep ordering of Fuse results for initial order in Ag-Grid
    return sideLoadIds
      .map(id => sideLoadedBusinesses[id])
      .filter(Boolean)
      .map(business => ({
        ...business,
        matchedFields: matchedFieldsById.get(business.id) ?? EMPTY_ARRAY,
        validationIssues: validationIssuesById.get(business.id) ?? EMPTY_ARRAY,
      }));
  }, [
    showIgnored,
    sideLoadedBusinesses,
    sideLoadIds,
    matchedFieldsById,
    validationIssuesById,
  ]);

  const handleSelectAndCompare = useCallback(
    (selectedRecordId: string) => {
      if (selectedRecordId) {
        onSelect(selectedRecordId);
      }
    },
    [onSelect],
  );

  const context = useSideLoadedBusinessGridContext(handleSelectAndCompare);

  const standardPreamble =
    "Not finding what you're looking for? You can edit your search or fix spelling to see more";
  const noSearchTermsPreamble = "Enter search text to search suggestions";
  const noResultsPreamble =
    "No suggestions found. You can edit the search to fix spelling or include fewer words.";
  const noExactResultsPreamble =
    "No exact matches found. Searching for any of the entered terms instead. You can try fixing spelling, removing generic terms or including fewer terms to see exact matches.";

  let preamble = standardPreamble;
  if (debouncedSearchTerm.length === 0) {
    preamble = noSearchTermsPreamble;
  } else if (rowData.length === 0) {
    preamble = noResultsPreamble;
  } else if (sideLoadResults !== sideLoadStrictResults) {
    preamble = noExactResultsPreamble;
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle onClose={onClose}>Data Suggestions</DialogTitle>
      <DialogContent dividers>
        <Column full>
          <Row paddingVertical={1}>
            <SmallText>
              {preamble} Suggestions are sourced from your supplied Legacy
              Business data.
            </SmallText>
          </Row>
          <Row fullWidth alignCenter gridGap={1}>
            <SearchInput
              value={searchTerm}
              handleChange={setSearchTerm}
              style={{ marginLeft: 0 }}
            />
            {(ignoredCount > 0 || showIgnored) && (
              <SlimSecondaryButton
                onClick={() => setShowIgnored(!showIgnored)}
                type="button"
              >
                {showIgnored ? "Hide ignored" : `Show ${ignoredCount} ignored`}
              </SlimSecondaryButton>
            )}
          </Row>
          <AgGridTable
            columnDefs={SideLoadedBusinessColumnDefs}
            rowData={rowData}
            getRowId={getSideLoadedBusinessRowId}
            hasExportCsvPermissions={false}
            tableName={AgGridTables.SIDELOADED_BUSINESSES}
            WrapperComponent={SideLoadedBusinessTableWrapper}
            showGlobalSearchFilter={false}
            hideSavedViews
            context={context}
          />
        </Column>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
};

export default SideLoadedBusinessTable;
